import axios from 'axios'
import { defineStore } from 'pinia'
import { logJournalHttpError } from '../composables/useErrorHandler'
import { normalizeFilterChoice } from '../utils/normalizeFilterChoice'
import { BASE_URL } from '@/environment/environment'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'

const API_BASE = `${BASE_URL}/api/journal/journal`
const FILTER_CHOICES_URL = `${API_BASE}/filter-choices/`

const ALLOWED_FIELDS = ['departments', 'responsible_employees', 'created_by'] as const

export const useJournalsListFilterStore = defineStore('journalsListFilter', {
  state: () => ({
    filterChoices: {} as Record<string, IFilterChoiceItem[]>,
    searchChoices: {} as Record<string, IFilterChoiceItem[]>,
    filterCursors: {} as Record<string, string | null>,
    filterLoading: {} as Record<string, boolean>,
    filterStrategies: {} as Record<string, string>,
    filters: {
      departments: [] as string[],
      responsible_employees: [] as string[],
      created_by: [] as string[],
      created_at: null as { from?: string; to?: string } | null,
    },
  }),

  actions: {
    async fetchFilterChoices(
      module: 'adverse' | 'ticket' | 'document' | 'journal',
      fieldKey: string,
      search = '',
      cursor?: string,
    ): Promise<void> {
      if (module !== 'journal' || !ALLOWED_FIELDS.includes(fieldKey as any)) {
        return
      }

      this.filterLoading[fieldKey] = true
      try {
        const field = fieldKey === 'created_by' ? 'creators' : fieldKey
        const params: Record<string, string> = { field }
        if (search) {
          params.search_field = search
        }
        if (cursor) {
          params.cursor = cursor
        }

        if (fieldKey !== 'departments' && this.filters.departments?.length) {
          params.departments = this.filters.departments.join(',')
        }
        if (fieldKey !== 'responsible_employees' && this.filters.responsible_employees?.length) {
          params.responsible_employees = this.filters.responsible_employees.join(',')
        }
        if (fieldKey !== 'created_by' && this.filters.created_by?.length) {
          params.creators = this.filters.created_by.join(',')
        }

        const { data } = await axios.get<{ results?: unknown[]; next?: string | null; strategy?: string }>(
          FILTER_CHOICES_URL,
          { params },
        )

        const results: IFilterChoiceItem[] = (data.results || []).map((r: any) => normalizeFilterChoice(r))

        this.filterStrategies[fieldKey] = data.strategy || 'independent'

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }
        this.filterCursors[fieldKey] = data.next ?? null
      } catch (e) {
        console.error(`[journalsListFilter] fetchFilterChoices [${fieldKey}]:`, e)
        logJournalHttpError(e, {
          function: 'fetchFilterChoices',
          store: 'journalsListFilterStore',
          payload: { fieldKey, search, cursor },
        })
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    resetTreeFilters() {
      this.filters.departments = []
      this.filters.responsible_employees = []
      this.filters.created_by = []
      this.filters.created_at = null
    },
  },
})
